import { useEffect, useRef, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type { ContentTypeAgentMention } from '@confluence/content-types-bridge/entry-points/content-types-shell-types.ts';
import { useAIRovoButtonEnablement } from '@confluence/ai-common/entry-points/useAIRovoButtonEnablement.ts';
import {
	type AdfDocument,
	handleAgentMentionInComment,
} from '@confluence/agent-mention/entry-points/handleAgentMentionInComment.ts';
import { queryAgentMentions } from '@confluence/fabric-media-support/entry-points/queryAgentMentions.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

export type DatabaseAgentMentionService = {
	getAgentMentions: (query: string, maxResults: number) => Promise<ContentTypeAgentMention[]>;
	onCommentSaved: (input: { adf: object; commentId: string }) => void;
};

type UseDatabaseAgentMentionServiceOptions = {
	contentId: string;
};

const isAdfDocument = (adf: unknown): adf is AdfDocument =>
	typeof adf === 'object' && adf !== null && (adf as { type?: unknown }).type === 'doc';

/**
 * Lets the database's own comment UI offer agents in its typeahead and start an agent after a
 * comment saves, the same way Confluence's comment editor and comments panel do. Matches
 * `useWhiteboardAgentMentionService` in `whiteboard-common`.
 */
export const useDatabaseAgentMentionService = ({
	contentId,
}: UseDatabaseAgentMentionServiceOptions): DatabaseAgentMentionService => {
	const { userId, cloudId, activationId, isAIEarlyAccessEnabled } = useSessionData();
	const isRovoButtonEnabled = useAIRovoButtonEnablement();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Canvas binds these methods once, so the service reads the latest values through a ref.
	const latest = {
		contentId,
		userId,
		cloudId,
		activationId,
		isAIEarlyAccessEnabled,
		isRovoButtonEnabled,
		createAnalyticsEvent,
	};
	const latestRef = useRef(latest);
	useEffect(() => {
		latestRef.current = latest;
	});

	const [service] = useState<DatabaseAgentMentionService>(() => ({
		getAgentMentions: async (query, maxResults) => {
			try {
				// Same checks as the agent list in Confluence's comment editor.
				const current = latestRef.current;
				if (
					!current.cloudId ||
					!current.userId ||
					!current.isRovoButtonEnabled ||
					!current.isAIEarlyAccessEnabled
				) {
					return [];
				}
				// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
				if (!fg('canvas-rovo-comment-sessions')) {
					return [];
				}
				if (!expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true)) {
					return [];
				}

				const { mentions } = await queryAgentMentions(query, maxResults);
				return mentions.flatMap((mention) =>
					mention.isPlaceholder
						? []
						: [
								{
									id: mention.id,
									name: mention.name,
									mentionName: mention.mentionName,
									avatarUrl: mention.avatarUrl,
									description: mention.description,
									userType: mention.userType,
									appType: mention.appType ?? undefined,
								},
							],
				);
			} catch {
				// queryAgentMentions already reports the error to monitoring.
				return [];
			}
		},
		onCommentSaved: (input) => {
			try {
				const current = latestRef.current;
				const adf = input?.adf;
				const commentId = input?.commentId;
				if (!current.cloudId || !current.contentId || !commentId || !isAdfDocument(adf)) {
					return;
				}
				// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
				if (!fg('canvas-rovo-comment-sessions')) {
					return;
				}
				if (!expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true)) {
					return;
				}

				const accountId =
					current.userId && current.userId !== 'unidentified' ? current.userId : undefined;
				// Returns early when the comment has no agent mention. A database with no activation ID
				// gets no tags.
				handleAgentMentionInComment(
					adf,
					current.cloudId,
					current.contentId,
					commentId,
					accountId,
					'inline',
					current.createAnalyticsEvent,
					{
						contentType: 'database',
						pageContext: 'confluence-page-view',
						activationId: current.activationId,
					},
				);
			} catch {
				// Never throw back into the embedded content.
			}
		},
	}));

	return service;
};
